import { observer } from 'mobx-react';
import { FC, Fragment, useEffect } from 'react';

import {
    Banner,
    Button,
    ButtonToggle,
    Form,
    Link,
    Modal,
    Stack,
    Text,
} from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';

import { PhoneNumbersStore } from '../../stores/phone-numbers.store';
import { BrandStatus, BrandUtility } from '@servicetitan/application-data/telecom';
import { FeaturesStore } from '@servicetitan/application-data/features';

interface NumberSearchModalProps {
    tfnDisabled?: boolean;
    onStepBack?(): void;
}

export const NumberSearch: FC<NumberSearchModalProps> = observer(({ onStepBack, tfnDisabled }) => {
    const [store, { features }] = useDependencies(PhoneNumbersStore, FeaturesStore);
    const { isLocal, areaCode, zipCode, contains } = store.formState.$.searchFormState.$;
    const onNumberTypeChange = (value: boolean | undefined) => {
        isLocal.onChange(value!);
    };

    useEffect(() => {
        store.loadBrandData();
    }, [store]);

    return (
        <Fragment>
            <Modal.Header title="Add New Number" onClose={store.closeModal} />
            <Modal.Content>
                <Form>
                    {store.isRegistrationForTextingNotStarted() ? (
                        <Banner
                            status="warning"
                            icon
                            title="Complete Register for Texting"
                            primaryActionName="Register Now"
                            onPrimaryActionClick={() => {
                                window.open(`#/Settings/BrandRegistration`, '_blank');
                            }}
                            className="m-b-3"
                        >
                            The telecommunication industry is introducing new protocols designed to
                            reduce spam and increase reliability and throughput involved with text
                            messaging. In order to send any text messages through Service Titan,
                            using either a 10-digit local number or a toll-free number, you must
                            complete the{' '}
                            <Link
                                onClick={() => {
                                    window.open(`#/Settings/BrandRegistration`, '_blank');
                                }}
                            >
                                <b>
                                    <u>Register for Texting</u>
                                </b>
                            </Link>{' '}
                            form. To learn more, please read{' '}
                            <Link
                                onClick={() => {
                                    window.open(
                                        `https://help.servicetitan.com/how-to/how-to-tcr-registration`,
                                        '_blank'
                                    );
                                }}
                            >
                                <b>
                                    <u>this article</u>
                                </b>
                            </Link>{' '}
                            on verification/approval timeline and fees, which are subject to change.
                        </Banner>
                    ) : (
                        features.EnableTollFreeNumberVerificationStatus &&
                        store.savedBrandModel &&
                        store.savedBrandModel.status === BrandStatus.SuccessfullyCreated &&
                        !BrandUtility.areYourInformationFieldsFilled(store.savedBrandModel) && (
                            <Banner
                                status="warning"
                                icon
                                title="Provide Missing Information for Register for Texting"
                                className="m-b-3"
                            >
                                Your company has completed the Register for Texting, but we need
                                more information due to addition of new fields. You can also update
                                via{' '}
                                <Link
                                    onClick={() => {
                                        window.open(`#/Settings/BrandRegistration`, '_blank');
                                    }}
                                >
                                    {' '}
                                    <b>Settings {'>'} Register for Texting.</b>
                                </Link>
                            </Banner>
                        )
                    )}
                    <ButtonToggle
                        className="m-b-3"
                        onChange={onNumberTypeChange}
                        options={[
                            {
                                text: 'Local Number',
                                value: true,
                                disabled: false,
                            },
                            {
                                text: 'Toll-Free Number',
                                value: false,
                                disabled: tfnDisabled,
                            },
                        ]}
                        value={isLocal.value}
                    />
                    {isLocal.value && (
                        <Stack justifyContent="space-between" className="m-b-3">
                            <Form.Input
                                className="m-b-0-i"
                                label={
                                    <Text size={2} bold className="m-b-1-i">
                                        Area Code
                                    </Text>
                                }
                                placeholder="626"
                                value={areaCode.value}
                                onChange={areaCode.onChangeHandler}
                            />
                            <Stack.Item alignSelf="center">
                                <Text size={2} className="m-t-3-i">
                                    and/or
                                </Text>
                            </Stack.Item>
                            <Form.Input
                                label={
                                    <Text size={2} bold className="m-b-1-i">
                                        Zip
                                    </Text>
                                }
                                placeholder="90029"
                                value={zipCode.value}
                                onChange={zipCode.onChangeHandler}
                            />
                        </Stack>
                    )}
                    <Form.Input
                        label={
                            <Text el="span" size={2} bold>
                                Contains{' '}
                                <Text el="span" size={2} subdued>
                                    (optional)
                                </Text>
                            </Text>
                        }
                        value={contains.value}
                        onChange={contains.onChangeHandler}
                    />
                </Form>
            </Modal.Content>
            <Modal.Footer align={onStepBack ? 'space-between' : 'right'}>
                {onStepBack && <Button onClick={onStepBack}>Back</Button>}
                <Button
                    primary
                    onClick={store.searchNumbers}
                    disabled={
                        store.searchInProgress ||
                        (isLocal.value && !areaCode.value && !zipCode.value)
                    }
                >
                    Search Numbers
                </Button>
            </Modal.Footer>
        </Fragment>
    );
});
